import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Link } from 'react-router-dom';
import BrandLogo from '@/components/BrandLogo';
import { Eye, EyeOff, Package, TrendingUp, Truck } from 'lucide-react';

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const user = await login(email, password);
      if (!user?.role) {
        setError('Signed in but profile could not be loaded. Please try again.');
        return;
      }
      /* PublicRoute redirects to /dashboard or /admin/dashboard once user is in context. */
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg =
        typeof d === 'string'
          ? d
          : Array.isArray(d)
            ? d.map((x) => (typeof x === 'object' && x?.msg ? x.msg : String(x))).join(' ')
            : null;
      setError(msg || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-white">
      {/* Left - Form */}
      <div className="flex-1 flex flex-col justify-center px-8 sm:px-12 lg:px-16 max-w-lg w-full">
        {/* Logo */}
        <div className="mb-10">
          <div className="mb-8">
            <BrandLogo variant="full" className="max-w-[min(100%,320px)]" />
          </div>
          <h1 className="font-heading text-4xl font-bold text-slate-900 tracking-tight mb-2">Welcome back</h1>
          <p className="text-slate-500 text-sm font-body">Sign in to your unified B2B portal</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
          <div>
            <label className="block text-xs font-semibold text-slate-700 uppercase tracking-widest mb-1.5" htmlFor="email">
              Email Address
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@company.com"
              data-testid="login-email-input"
              className="w-full h-11 px-3.5 rounded-lg border border-slate-300 text-sm bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900 transition-colors"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-700 uppercase tracking-widest mb-1.5" htmlFor="password">
              Password
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPass ? 'text' : 'password'}
                autoComplete="current-password"
                required
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="login-password-input"
                className="w-full h-11 px-3.5 pr-11 rounded-lg border border-slate-300 text-sm bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900 transition-colors"
              />
              <button
                type="button"
                onClick={() => setShowPass(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                data-testid="toggle-password-visibility"
              >
                {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3" data-testid="login-error">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit-button"
            className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Signing in...
              </>
            ) : 'Sign In'}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-slate-600">
          Don&apos;t have an account?{' '}
          <Link to="/register" className="font-semibold text-sky-600 hover:text-sky-700" data-testid="login-register-link">
            Create account
          </Link>
        </p>
      </div>

      {/* Right - Visual */}
      <div className="hidden lg:flex flex-1 bg-slate-900 relative overflow-hidden items-center justify-center p-12">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url("https://images.unsplash.com/photo-1765046255517-412341954c4c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <BrandLogo variant="full" display="watermark" />
        <div className="relative z-10 max-w-md text-center">
          <h2 className="font-heading text-3xl font-bold text-white mb-4 tracking-tight">
            Your Unified B2B<br />Command Center
          </h2>
          <p className="text-slate-400 text-sm font-body leading-relaxed mb-8">
            Manage all your trade operations, commodity bids, and logistics from one powerful platform.
          </p>
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3 bg-white/5 rounded-xl px-4 py-3 border border-white/10">
              <div className="w-8 h-8 bg-sky-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <Package className="w-4 h-4 text-sky-400" />
              </div>
              <div className="text-left">
                <p className="text-white text-sm font-semibold">DeMaxtore</p>
                <p className="text-slate-400 text-xs">Global B2B Supply & Export</p>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-white/5 rounded-xl px-4 py-3 border border-white/10">
              <div className="w-8 h-8 bg-orange-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-4 h-4 text-orange-400" />
              </div>
              <div className="text-left">
                <p className="text-white text-sm font-semibold">CommodityBid</p>
                <p className="text-slate-400 text-xs">Live Commodity Auctions</p>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-white/5 rounded-xl px-4 py-3 border border-white/10">
              <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <Truck className="w-4 h-4 text-green-400" />
              </div>
              <div className="text-left">
                <p className="text-white text-sm font-semibold">FreightIQ</p>
                <p className="text-slate-400 text-xs">Freight & Logistics Management</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
